import { format } from 'date-fns';

/**
 * The one timezone every wall-clock value in the product is read and written
 * in (RCOMM-6264). An analyst in Caracas sees, types and filters the hours an
 * item aired in Spain, exactly as an analyst in Madrid does.
 *
 * Every `Date` elsewhere in the app is a real instant. This module is the only
 * place that turns an instant into a Madrid hour and back.
 *
 * Nothing here reads the browser's clock. Madrid's fields come from `Intl`,
 * which resolves them per instant, and are formatted through a `Date` whose
 * local getters read UTC, a zone with no DST. `date-fns-tz` is not used: its
 * `formatInTimeZone` goes through a browser-local `Date`, so a Madrid hour that
 * falls in the browser's own DST gap is printed an hour off, and its offset
 * helpers read local fields too.
 */
export const APP_TIMEZONE = 'Europe/Madrid';

type TDateInput = Date | string | number;

const MILLISECONDS_IN_SECOND = 1000;
const MILLISECONDS_IN_MINUTE = 60 * MILLISECONDS_IN_SECOND;
const HALF_A_DAY_IN_MILLISECONDS = 12 * 60 * MILLISECONDS_IN_MINUTE;
const START_OF_DAY = '00:00:00';
const END_OF_DAY = '23:59:59.999';

/** `2026-09-30`, `2026-09-30T10:00`, `2026-09-30 10:00:00.000` — no offset. */
const NAIVE_DATETIME_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:[.,](\d+))?)?)?$/;

const appTimezoneFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: APP_TIMEZONE,
  hourCycle: 'h23',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

type TWallClockFields = {
  year: number;
  month: number;
  day: number;
  hours: number;
  minutes: number;
  seconds: number;
  milliseconds: number;
};

function toInstant(input: TDateInput): Date {
  return input instanceof Date ? input : new Date(input);
}

function getWallClockFields(time: number): TWallClockFields {
  const parts: Record<string, number> = {};

  appTimezoneFormatter.formatToParts(new Date(time)).forEach(({ type, value }) => {
    parts[type] = Number(value);
  });

  return {
    year: parts.year,
    month: parts.month,
    day: parts.day,
    // Some engines still write midnight as `24` despite `h23`.
    hours: parts.hour % 24,
    minutes: parts.minute,
    seconds: parts.second,
    milliseconds:
      ((time % MILLISECONDS_IN_SECOND) + MILLISECONDS_IN_SECOND) % MILLISECONDS_IN_SECOND,
  };
}

/** The wall clock as if it were UTC: `10:00` Madrid becomes the number of `10:00Z`. */
function encodeWallClock({
  year,
  month,
  day,
  hours,
  minutes,
  seconds,
  milliseconds,
}: TWallClockFields) {
  return Date.UTC(year, month - 1, day, hours, minutes, seconds, milliseconds);
}

/** Madrid's offset from UTC at an instant, in milliseconds (`+02:00` is 7 200 000). */
function getOffsetAt(time: number): number {
  return encodeWallClock(getWallClockFields(time)) - time;
}

/**
 * The instant a Madrid wall clock names, resolved from the zone's own offsets.
 *
 * - An hour the autumn switch repeats resolves to its first occurrence.
 * - An hour the spring switch skips resolves with the summer offset: `02:30`
 *   on 2026-03-29 is `00:30Z`, which reads 01:30 Madrid.
 */
function wallClockToInstant(fields: TWallClockFields): Date {
  const encoded = encodeWallClock(fields);
  const candidateOffsets = [
    getOffsetAt(encoded - HALF_A_DAY_IN_MILLISECONDS),
    getOffsetAt(encoded + HALF_A_DAY_IN_MILLISECONDS),
  ];
  const validTimes = candidateOffsets
    .map(offset => encoded - offset)
    .filter(time => getOffsetAt(time) === encoded - time);

  if (validTimes.length) return new Date(Math.min(...validTimes));

  return new Date(encoded - Math.max(...candidateOffsets));
}

/**
 * A `Date` whose local getters read its UTC fields, so date-fns formats an
 * encoded wall clock (see `encodeWallClock`) with no timezone involved. date-fns
 * 4 builds intermediate dates with `new date.constructor(...)`, so they keep
 * this behaviour.
 */
class EncodedWallClockDate extends Date {
  getFullYear() {
    return this.getUTCFullYear();
  }
  getMonth() {
    return this.getUTCMonth();
  }
  getDate() {
    return this.getUTCDate();
  }
  getDay() {
    return this.getUTCDay();
  }
  getHours() {
    return this.getUTCHours();
  }
  getMinutes() {
    return this.getUTCMinutes();
  }
  getSeconds() {
    return this.getUTCSeconds();
  }
  getMilliseconds() {
    return this.getUTCMilliseconds();
  }
  getTimezoneOffset() {
    return 0;
  }
}

/** Formats the instant as the Madrid wall clock, with date-fns patterns. */
export function formatInAppTz(input: TDateInput, pattern: string): string {
  const time = toInstant(input).getTime();

  if (Number.isNaN(time)) throw new RangeError('Invalid time value');

  return format(new EncodedWallClockDate(encodeWallClock(getWallClockFields(time))), pattern);
}

export type TAppWallClockParts = {
  /** `2026-09-30` */
  date: string;
  hours: number;
  minutes: number;
  seconds: number;
  /** Crontab weekday: Sunday `0` through Saturday `6`. */
  weekday: number;
};

/** The Madrid wall-clock fields of an instant, for logic that needs them. */
export function appWallClockParts(input: TDateInput): TAppWallClockParts {
  const fields = getWallClockFields(toInstant(input).getTime());
  const encoded = new Date(encodeWallClock(fields));

  return {
    date: encoded.toISOString().slice(0, 10),
    hours: fields.hours,
    minutes: fields.minutes,
    seconds: fields.seconds,
    weekday: encoded.getUTCDay(),
  };
}

export function secondsOfDayInAppTz(input: TDateInput): number {
  const { hours, minutes, seconds } = appWallClockParts(input);

  return hours * 3600 + minutes * 60 + seconds;
}

/**
 * Writes an instant as Madrid wall clock with no offset —
 * `2026-09-30T10:00:00.000` — for the contracts that expect Spanish time
 * without a zone (CNMC: its backend stores the text and forwards its fields to
 * Media IA as Spanish time). Read it back with `parseAppWallClock`.
 */
export function toAppWallClockString(input: TDateInput): string {
  return formatInAppTz(input, "yyyy-MM-dd'T'HH:mm:ss.SSS");
}

/**
 * Reads a datetime written as Madrid wall clock with no offset — CNMC's
 * `2026-09-30T10:00:00.000` — as the instant it names, whatever the browser's
 * timezone. A string that does carry an offset or `Z` keeps it.
 */
export function parseAppWallClock(naive: string): Date {
  const match = NAIVE_DATETIME_PATTERN.exec(naive.trim());

  if (!match) return new Date(naive);

  const [year, month, day, hours, minutes, seconds] = match
    .slice(1, 7)
    .map(part => Number(part ?? 0));
  const milliseconds = Number((match[7] ?? '0').slice(0, 3).padEnd(3, '0'));

  return wallClockToInstant({ year, month, day, hours, minutes, seconds, milliseconds });
}

/**
 * The instant a Madrid wall clock names: `("2026-09-30", "10:00:00")` is
 * `2026-09-30T08:00:00.000Z`. `hms` may be `HH:mm`, `HH:mm:ss` or carry
 * milliseconds. A skipped spring hour (`02:30` on 2026-03-29) is `00:30Z`; a
 * repeated autumn hour (`02:30` on 2026-10-25) is its first occurrence, `00:30Z`.
 */
export function appWallClockToInstant(ymd: string, hms: string): Date {
  return parseAppWallClock(`${ymd}T${hms}`);
}

/** The instant of 00:00:00.000 of the Madrid day `input` falls on. */
export function startOfAppDay(input: TDateInput = new Date()): Date {
  return appWallClockToInstant(appWallClockParts(input).date, START_OF_DAY);
}

/** The instant of 23:59:59.999 of the Madrid day `input` falls on. */
export function endOfAppDay(input: TDateInput = new Date()): Date {
  return appWallClockToInstant(appWallClockParts(input).date, END_OF_DAY);
}

/** Adds calendar days to a `yyyy-MM-dd` day, with no timezone involved. */
export function addAppDays(ymd: string, days: number): string {
  const [year, month, day] = ymd.split('-').map(Number);

  return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10);
}

/** Crontab weekday (Sunday `0` … Saturday `6`) of a `yyyy-MM-dd` day. */
export function getAppDayWeekday(ymd: string): number {
  const [year, month, day] = ymd.split('-').map(Number);

  return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
}

/** Drops seconds and milliseconds: two emissions are the same within the minute. */
export function floorToMinute(input: TDateInput): Date {
  const time = toInstant(input).getTime();

  return new Date(Math.floor(time / MILLISECONDS_IN_MINUTE) * MILLISECONDS_IN_MINUTE);
}

/**
 * INPUT EDGE ONLY. A `Date` whose browser-clock fields spell the Madrid wall
 * clock of `input`, for design-system pickers that only work in the browser's
 * clock. It is not the instant `input` names: never store, compare or send it.
 * Convert what the picker emits back with `fromAppWallClockEdge`.
 */
export function toAppWallClock(input: TDateInput): Date {
  const time = toInstant(input).getTime();

  if (Number.isNaN(time)) return new Date(NaN);

  const { year, month, day, hours, minutes, seconds, milliseconds } = getWallClockFields(time);

  return new Date(year, month - 1, day, hours, minutes, seconds, milliseconds);
}

/**
 * INPUT EDGE ONLY. The instant named by a `Date` whose browser-clock fields
 * spell a Madrid wall clock — what a design-system picker emits.
 */
export function fromAppWallClock(wallClock: Date): Date {
  if (Number.isNaN(wallClock.getTime())) return new Date(NaN);

  return wallClockToInstant({
    year: wallClock.getFullYear(),
    month: wallClock.getMonth() + 1,
    day: wallClock.getDate(),
    hours: wallClock.getHours(),
    minutes: wallClock.getMinutes(),
    seconds: wallClock.getSeconds(),
    milliseconds: wallClock.getMilliseconds(),
  });
}

/**
 * INPUT EDGE ONLY. Converts what a picker emitted back to an instant, returning
 * `original` untouched when the picker handed back the very value it was given.
 *
 * The shim is not a pure round trip when the browser zone has a DST switch of
 * its own: a Madrid wall clock inside the browser's gap (Madrid 02:30 on
 * 2026-03-08 in New York) is normalised by the browser an hour forward, and
 * converting it back would move an unedited value by that hour.
 */
export function fromAppWallClockEdge(
  emitted: Date,
  passed: Date | null | undefined,
  original: Date | null | undefined
): Date {
  if (original && passed && emitted.getTime() === passed.getTime()) return original;

  return fromAppWallClock(emitted);
}
